import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage, IntlShape } from 'react-intl';
import ChevronSVG from '../../../images/svg/icons8-expand-arrow';
import { Popover } from '../../global/popover/popover';
import Text from '../../global/text/text';
import { SearchInput } from '../../global/search-input/search-input';
import { IEntityRoleDTO } from '../../../api/events.dao';

interface IDeliveryTeamRoleProps {
    intl: IntlShape;
    entityRoleKey: string;
    intlEntityRoleKey: string;
    entityRolesList: IEntityRoleDTO[];
    setEntityRole: (username: string, role: string) => void;
    entityRole: string | null;
    shouldRenderIcon: boolean;
    customPopoverWidth?: number;
    customLeft?: number;
    withSearchInput?: boolean;
}

const MultiSelectRoleDropdown = (props: IDeliveryTeamRoleProps) => {
    const {
        entityRole,
        entityRolesList,
        intlEntityRoleKey,
        setEntityRole,
        entityRoleKey,
        intl,
        shouldRenderIcon,
        customPopoverWidth,
        customLeft,
        withSearchInput,
    } = props;
    const roleButtonRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [filteredItems, setFilteredItems] = useState<IEntityRoleDTO[]>(entityRolesList);
    const shouldDisabledItems = shouldRenderIcon === false;

    const handleSearchRole = (searchValue: string) => {
        const newItems = entityRolesList.filter(item => item.label.toLowerCase().indexOf(searchValue.trim().toLowerCase()) !== -1);
        if (!searchValue) {
            setFilteredItems(entityRolesList);
        } else {
            setFilteredItems(newItems);
        }
    };

    const handleOptionClick = (id: string, optionValue: string) => {
        setEntityRole(id, optionValue);
        setFilteredItems(entityRolesList);
        setIsModalOpen(false);
    };

    return (
        <div
            className={classnames('item-role', {
                'item-role--selected': entityRole && !shouldDisabledItems,
                'item-role--active': isModalOpen,
                'item-role--disabled': shouldDisabledItems,
            })}
            ref={roleButtonRef}
            onClick={!shouldDisabledItems ? () => setIsModalOpen(prevState => !prevState) : undefined}
        >
            <div
                className='item-role__label'
            >
                <Text
                    message={entityRole
                            ? intl.formatMessage({
                                id: `oe.${intlEntityRoleKey}.${entityRole}`,
                            })
                            : shouldDisabledItems ? ''
                            : intl.formatMessage({
                                id: 'oe.common.assignRole',
                                description: 'Role option',
                                defaultMessage: 'Assign role...',
                            })}
                    withEllipsis
                />
                {!shouldDisabledItems && <ChevronSVG />}
            </div>
            <Popover
                padding={0}
                isOpen={isModalOpen}
                buttonRef={roleButtonRef}
                onClickOutside={() => setIsModalOpen(false)}
                customWidth={customPopoverWidth || roleButtonRef.current?.getBoundingClientRect().width}
                customLeft={customLeft}
            >
                <div className='item-role__dropdown--wrapper'>
                    {withSearchInput && <SearchInput onChange={e => handleSearchRole(e.target.value)} autoFocus={true} />}


                    <div className='item-role__dropdown--options'>
                        {filteredItems.length ? (
                            filteredItems.map(({ id }) => (
                                <div
                                    key={id}
                                    className={classnames('item-role__dropdown--option', {
                                        'item-role__dropdown--option--selected': id === entityRole
                                    })}
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleOptionClick(entityRoleKey, id);
                                    }}
                                >
                                    <FormattedMessage
                                        id={`oe.${intlEntityRoleKey}.${id}`}
                                        description='Operator role option'
                                    />
                                </div>
                            ))
                        ) : (
                            <div className='item-role__dropdown--option item-role__dropdown--option__empty'>
                                {intl.formatMessage({
                                    id: 'oe.items.noMatchingSearchCriteria',
                                    description: 'Label text',
                                    defaultMessage: 'No items matching search criteria'
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export { MultiSelectRoleDropdown };
